import React, { useMemo } from 'react';
import { ChartColor } from '@t-one-internal/design-system';

interface FeedbackActivityRow {
  feedbackGiven: number;
  feedbackReceived: number;
  pending360: number;
}

interface FeedbackActivityChartProps {
  data: Array<Record<string, unknown>>;
  cycleName?: string;
}

const toFeedbackRow = (raw: Record<string, unknown>): FeedbackActivityRow => ({
  feedbackGiven: Number(raw.feedbackGiven ?? raw.feedback_given ?? 0),
  feedbackReceived: Number(raw.feedbackReceived ?? raw.feedback_received ?? 0),
  pending360: Number(raw.pending360 ?? raw.pending_360 ?? 0),
});

const StatCard: React.FC<{
  value: number;
  label: string;
  color: string;
  borderColor: string;
}> = ({ value, label, color, borderColor }) => (
  <div
    style={{
      flex: 1,
      backgroundColor: 'white',
      borderRadius: '12px',
      border: `1px solid ${borderColor}`,
      padding: '24px 16px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
      minWidth: 0,
    }}
  >
    <span
      style={{
        fontSize: '36px',
        fontWeight: 700,
        color,
        lineHeight: 1,
        fontVariantNumeric: 'tabular-nums',
      }}
    >
      {value}
    </span>
    <span style={{ fontSize: '13px', color: '#6B7280', fontWeight: 500 }}>
      {label}
    </span>
  </div>
);

export const FeedbackActivityChart: React.FC<FeedbackActivityChartProps> = ({ data }) => {
  const stats = useMemo(() => {
    if (!data || !Array.isArray(data) || data.length === 0) return null;
    return toFeedbackRow(data[0]);
  }, [data]);

  if (!stats) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          color: '#9CA3AF',
          fontSize: '13px',
        }}
      >
        No feedback activity data available
      </div>
    );
  }

  const total = stats.feedbackGiven + stats.feedbackReceived + stats.pending360;

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        gap: '20px',
      }}
    >
      {/* KPI stat cards */}
      <div style={{ display: 'flex', gap: '12px', width: '100%' }}>
        <StatCard value={stats.feedbackGiven} label="Given" color={ChartColor.blue} borderColor="#DBEAFE" />
        <StatCard value={stats.feedbackReceived} label="Received" color={ChartColor.emerald} borderColor="#D1FAE5" />
        <StatCard value={stats.pending360} label="360 Pending" color={ChartColor.amber} borderColor="#FDE68A" />
      </div>

      {/* Activity bar */}
      {total > 0 && (
        <div style={{ width: '100%' }}>
          <div style={{ display: 'flex', height: '8px', borderRadius: '4px', overflow: 'hidden', backgroundColor: '#F3F4F6' }}>
            {stats.feedbackGiven > 0 && (
              <div style={{ width: `${(stats.feedbackGiven / total) * 100}%`, backgroundColor: ChartColor.blue, transition: 'width 0.4s ease' }} />
            )}
            {stats.feedbackReceived > 0 && (
              <div style={{ width: `${(stats.feedbackReceived / total) * 100}%`, backgroundColor: ChartColor.emerald, transition: 'width 0.4s ease' }} />
            )}
            {stats.pending360 > 0 && (
              <div style={{ width: `${(stats.pending360 / total) * 100}%`, backgroundColor: ChartColor.amber, transition: 'width 0.4s ease' }} />
            )}
          </div>
          <div style={{ display: 'flex', justifyContent: 'center', gap: '16px', marginTop: '8px' }}>
            <span style={{ fontSize: '11px', color: '#6B7280', display: 'flex', alignItems: 'center', gap: '4px' }}>
              <span style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: ChartColor.blue, display: 'inline-block' }} />
              Given
            </span>
            <span style={{ fontSize: '11px', color: '#6B7280', display: 'flex', alignItems: 'center', gap: '4px' }}>
              <span style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: ChartColor.emerald, display: 'inline-block' }} />
              Received
            </span>
            <span style={{ fontSize: '11px', color: '#6B7280', display: 'flex', alignItems: 'center', gap: '4px' }}>
              <span style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: ChartColor.amber, display: 'inline-block' }} />
              360 Pending
            </span>
          </div>
        </div>
      )}
    </div>
  );
};
